// ChatBot.tsx
import React, { useState, useEffect, useRef } from 'react';
import { TbMessageChatbot } from 'react-icons/tb';

import { predefinedResponses, PredefinedResponse } from './predefinedResponses';
import Fuse from 'fuse.js';
import botProfileImage from './bot-profile.png';
import ChatBotModal from "../ChatBotModal"; // 봇 프로필 이미지 경로를 넣어주세요.

export default function ChatBot() {
  const [openbot, setOpenBot] = useState<boolean>(false);
  const [messages, setMessages] = useState<{ sender: string; text: string }[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fuse.js 설정
  const fuse = new Fuse(predefinedResponses, {
    keys: ['patterns'],
    threshold: 0.6, // 유사도 임계값 (0: 완벽한 일치, 1: 완전 불일치)
  });

  const openChatbot = () => {
    setOpenBot(true);
  };

  const closeChatbot = () => {
    setOpenBot(false);
    setMessages([]); // 챗봇 종료 시 메시지 초기화 (원하는 경우에만)
  };

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();
    if (trimmedMessage.length === 0) return;

    if (trimmedMessage.length > 500) {
      const warningMessage = { sender: 'bot', text: '메시지가 너무 깁니다. 500자 이하로 입력해주세요.' };
      setMessages((prevMessages) => [...prevMessages, warningMessage]);
      setInputMessage('');
      return;
    }

    const userMessage = { sender: 'user', text: trimmedMessage };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputMessage('');

    // Fuse.js를 사용한 유사도 매칭
    const searchResult = fuse.search(trimmedMessage);
    console.log("Fuse search result:", searchResult); // 디버깅을 위해 결과 출력

    let matchedResponse: PredefinedResponse | undefined;

    if (searchResult.length > 0 && searchResult[0].score !== undefined && searchResult[0].score < 0.8) {
      matchedResponse = searchResult[0].item as PredefinedResponse;
    } else {
      // 키워드 기반 매칭 시도
      const lowerCaseMessage = trimmedMessage.toLowerCase();
      matchedResponse = predefinedResponses.find(response =>
        response.keywords?.some(keyword => lowerCaseMessage.includes(keyword.toLowerCase()))
      );
    }

    if (matchedResponse) {
      const botResponse = { sender: 'bot', text: matchedResponse.response };
      setTimeout(() => {
        setMessages((prevMessages) => [...prevMessages, botResponse]);
      }, 1000);
    } else {
      // 백엔드 API 호출
      setIsLoading(true);
      try {
        const apiResponse = await fetch('/api/chatbot', { // 백엔드 API 엔드포인트 수정 필요
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: trimmedMessage }),
        });

        if (!apiResponse.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await apiResponse.json();
        const backendResponse = { sender: 'bot', text: data.response };
        setTimeout(() => {
          setMessages((prevMessages) => [...prevMessages, backendResponse]);
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error fetching chatbot response:', error);
        const errorResponse = { sender: 'bot', text: '죄송합니다. 서버에 문제가 발생했습니다. 나중에 다시 시도해주세요.' };
        setMessages((prevMessages) => [...prevMessages, errorResponse]);
        setIsLoading(false);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (openbot && messages.length === 0) {
      const initialBotMessage = { sender: 'bot', text: '안녕하세요! 무엇을 도와드릴까요?' };
      setMessages([initialBotMessage]);
    }
  }, [openbot]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div>
      {/* 챗봇 아이콘 */}
      <div className="fixed bottom-[80px] mobile:bottom-[55px] z-20 right-[20px] cursor-pointer overflow-hidden">
        <div
          className="bg-white text-MainTextColor transition duration-300 ease-in-out hover:text-MainColor border shadow hover rounded-full w-[45px] h-[45px] mobile:w-[38px] mobile:h-[38px] flex items-center justify-center"
          onClick={openChatbot}
        >
          <TbMessageChatbot className="text-[28px] mobile:text-2xl" />
        </div>
      </div>

      {/* 챗봇 모달 */}
      <ChatBotModal isOpen={openbot} onClose={closeChatbot}>
        <div className="bg-black py-2 rounded-t-lg p-4 h-[70px] ">
          <div className="flex items-center mobile:mt-[13px]">
            <div className="w-[35px]">
              <img
                className="rounded-full w-8 h-8" // 필요에 따라 크기 조정
                src={`${process.env.PUBLIC_URL}/favicon.png`}
                alt="크릿AI 프로필"
              />
            </div>
            <div className="flex flex-col">
              <span className="Pretendard-Bold text-white text-base mobile:text-sm ml-2">
                크릿AI와 대화하기
              </span>
              <p className="Pretendard-Regular text-white text-sm mobile:text-xs ml-2 mobile:mt-1 leading-8">
                💬 챗봇을 통해 문의를 해결해보세요!
              </p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white min-h-[600px]">
          <div className="mb-4">
            <p className="Pretendard-Regular text-sm text-gray-500">크릿 스토어 봇</p>
          </div>
          <section className="overflow-scroll h-[470px]">
            <div>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`mb-4 flex flex-col ${
                    message.sender === 'user' ? 'items-end' : 'items-start'
                  }`}
                >
                  {message.sender === 'bot' && (
                    <div className="ml-[45px] mb-1">
                      <span className="Pretendard-Bold text-sm text-gray-600">크릿AI</span>
                    </div>
                  )}
                  <div className="flex items-start">
                    {message.sender === 'bot' && (
                      <div className="w-8 h-8 rounded-full mr-2">
                        <img
                          className="rounded-full w-8 h-8"
                          src={`${process.env.PUBLIC_URL}/favicon.png`}
                          alt="Bot Profile"
                        />
                      </div>
                    )}
                    <div
                      className={`Pretendard-Medium inline-block px-5 py-2 rounded-[15px] max-w-[305px] break-words ${
                        message.sender === 'user'
                          ? 'bg-[#b6daff] text-[#111] text-right'
                          : 'bg-[#e9ebed] text-gray-900 text-left'
                      }`}
                    >
                      {message.text}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className={`mb-4 flex flex-col items-start`}>
                  <div className="mb-1">
                    <span className="text-sm text-gray-600">크릿AI</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-2 mt-1">
                      <img
                        className="rounded-full w-2 h-2"
                        src={`${process.env.PUBLIC_URL}/favicon.png`}
                        alt="Bot Profile"
                      />
                    </div>
                    <div className="inline-block px-4 py-2 rounded-full bg-[#e9ebed] text-gray-900">
                      응답을 생성 중입니다...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </section>
          <div className="mt-4 border-t pt-2 mobile:pt-3 flex">
            <input
              type="text"
              placeholder="Type a message"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              className="flex-1 border mobile:text-xs rounded-full px-4 py-2 focus:outline-none"
            />
            <button
              className="ml-2 px-4 py-2  bg-gray-800 hover:bg-opacity-80 transition duration-300 Pretendard-Medium mobile:text-xs  text-nowrap ease-in-out text-white rounded-full"
              onClick={handleSendMessage}
            >
              전송
            </button>
          </div>
        </div>
      </ChatBotModal>
    </div>
  );
}
